import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getUniversalFilterKey,
  getSessionUniversalFilterKey,
  getActivityKey,
  STORAGE_KEY_PREFIXES,
  FILTER_TYPES,
  FilterType,
} from 'src/contstant/storageKeys';

const TTL_HOURS = 4;

// Module-level flag to track if we've synced on this page load
// This resets on every page reload but persists during navigation
const syncedBusinessUnits = new Set<string>();

interface UseFilterPersistenceOptions<T> {
  storageKey: string;
  defaultFilters: T;
  excludeFields?: string[];
  universalFields?: string[]; // Fields stored in localStorage (divisions, locations)
  businessUnitId: string; // Required for BU-scoped keys
}

interface UseFilterPersistenceReturn<T> {
  filters: T;
  setFilters: (filters: T | ((prev: T) => T)) => void;
  clearFilters: () => void;
}

/**
 * Hook for persisting filters with hybrid storage approach and business-unit scoping
 *
 * Features:
 * - Universal filters (divisions, locations):
 *   - Persist in localStorage per business unit (source of truth)
 *   - Synced to sessionStorage on every page load (in loadFilters function)
 *   - Read from localStorage first, then cached in sessionStorage
 *   - Updates save to BOTH storages
 * - Page-specific filters → sessionStorage per business unit (tab-isolated, lost on tab close)
 * - 4-hour TTL per business unit with activity timestamp
 * - Sync on page reload (sessionStorage is checked on mount)
 * - Business unit change: reload page to load new BU's filters (no filter reset unless TTL expired)
 * - Automatic cleanup of expired data per business unit
 *
 * @param options Configuration options
 * @returns Filter state and setters
 */
export function useFilterPersistence<T extends Record<string, any>>({
  storageKey,
  defaultFilters,
  excludeFields = [],
  universalFields = [],
  businessUnitId,
}: UseFilterPersistenceOptions<T>): UseFilterPersistenceReturn<T> {
  // Use ref to track defaultFilters without causing re-renders
  const defaultFiltersRef = useRef(defaultFilters);
  useEffect(() => {
    defaultFiltersRef.current = defaultFilters;
  }, [defaultFilters]);

  const [filters, setFiltersState] = useState<T>(() => {
    return loadFilters(
      storageKey,
      defaultFilters,
      excludeFields,
      universalFields,
      businessUnitId,
    );
  });

  // Track if this is the initial mount and the previous businessUnitId
  const isInitialMount = useRef(true);
  const prevBusinessUnitIdRef = useRef<string>(businessUnitId);

  // Reload filters when businessUnitId becomes available or changes
  useEffect(() => {
    // Skip on initial mount - useState already loaded filters
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevBusinessUnitIdRef.current = businessUnitId;
      return;
    }

    // Only reload if businessUnitId actually changed (not just re-render with same value)
    if (businessUnitId && businessUnitId !== prevBusinessUnitIdRef.current) {
      // Remove previous BU from synced set to force re-sync from localStorage
      if (prevBusinessUnitIdRef.current) {
        syncedBusinessUnits.delete(prevBusinessUnitIdRef.current);
      }

      const reloadedFilters = loadFilters(
        storageKey,
        defaultFilters,
        excludeFields,
        universalFields,
        businessUnitId,
      );
      setFiltersState(reloadedFilters);
      prevBusinessUnitIdRef.current = businessUnitId;
    }
  }, [businessUnitId, storageKey, defaultFilters, excludeFields, universalFields]);

  // Update activity timestamp for this business unit whenever filters change
  const updateActivity = useCallback(() => {
    const now = Date.now();
    const activityKey = getActivityKey(businessUnitId);
    localStorage.setItem(activityKey, now.toString());
  }, [businessUnitId]);

  // Save filters to storage
  const setFilters = useCallback(
    (newFilters: T | ((prev: T) => T)) => {
      setFiltersState((prev) => {
        const updated = typeof newFilters === 'function' ? newFilters(prev) : newFilters;

        try {
          saveFiltersToStorage(updated, {
            storageKey,
            businessUnitId,
            excludeFields,
            universalFields,
          });
          updateActivity();
        } catch (error) {
          console.error('Failed to save filters:', error);
        }

        return updated;
      });
    },
    [storageKey, updateActivity, excludeFields, universalFields, businessUnitId],
  );

  // Clear filters for this business unit
  const clearFilters = useCallback(() => {
    // Create cleared state with null values for all clearable fields
    const clearedFilters = { ...defaultFiltersRef.current };
    Object.keys(clearedFilters).forEach((key) => {
      if (!excludeFields.includes(key)) {
        (clearedFilters as any)[key] = null;
      }
    });

    setFiltersState(clearedFilters as T);

    try {
      // Save cleared state (with null values) to sessionStorage
      const filtersToSave = { ...clearedFilters };
      excludeFields.forEach((field) => {
        delete filtersToSave[field as keyof T];
      });
      sessionStorage.setItem(storageKey, JSON.stringify(filtersToSave));

      // Clear universal filters by saving null (to distinguish from "never set")
      universalFields.forEach((field) => {
        saveUniversalFilter(businessUnitId, field, null);
      });

      // Update activity timestamp
      const activityKey = getActivityKey(businessUnitId);
      localStorage.setItem(activityKey, Date.now().toString());
    } catch (error) {
      console.error('Failed to clear filters:', error);
    }
  }, [storageKey, excludeFields, universalFields, businessUnitId]);

  return {
    filters,
    setFilters,
    clearFilters,
  };
}

/**
 * Get filter type from field name
 */
function getFilterType(field: string): FilterType {
  return field === FILTER_TYPES.LOCATIONS
    ? FILTER_TYPES.LOCATIONS
    : FILTER_TYPES.DIVISION;
}

/**
 * Save filters to storage with hybrid approach
 * - Universal filters → both localStorage and sessionStorage
 * - Page-specific filters → sessionStorage only
 */
function saveFiltersToStorage<T extends Record<string, any>>(
  filters: T,
  options: {
    storageKey: string;
    businessUnitId: string;
    excludeFields: string[];
    universalFields: string[];
  },
): void {
  const { storageKey, businessUnitId, excludeFields, universalFields } = options;

  // Separate page-specific filters
  const pageFiltersToSave: Partial<T> = {};
  Object.keys(filters).forEach((key) => {
    if (!excludeFields.includes(key) && !universalFields.includes(key)) {
      pageFiltersToSave[key as keyof T] = filters[key as keyof T];
    }
  });

  // Save universal filters to both storages
  universalFields.forEach((field) => {
    saveUniversalFilter(businessUnitId, field, filters[field as keyof T]);
  });

  // Save page-specific filters to sessionStorage
  if (Object.keys(pageFiltersToSave).length > 0) {
    sessionStorage.setItem(storageKey, JSON.stringify(pageFiltersToSave));
  } else {
    sessionStorage.removeItem(storageKey);
  }
}

/**
 * Save a single universal filter to both localStorage and sessionStorage
 * - null value = explicitly cleared (saved as null)
 * - undefined or empty array = remove from storage (never set)
 */
function saveUniversalFilter(businessUnitId: string, field: string, value: any): void {
  const filterType = getFilterType(field);
  const localKey = getUniversalFilterKey(businessUnitId, filterType);
  const sessionKey = getSessionUniversalFilterKey(businessUnitId, filterType);

  // null means explicitly cleared - save it
  if (value === null) {
    const serialized = JSON.stringify(null);
    localStorage.setItem(localKey, serialized);
    sessionStorage.setItem(sessionKey, serialized);
    return;
  }

  // undefined or empty array means never set - remove from storage
  const isEmpty = value === undefined || (Array.isArray(value) && value.length === 0);

  if (isEmpty) {
    localStorage.removeItem(localKey);
    sessionStorage.removeItem(sessionKey);
  } else {
    const serialized = JSON.stringify(value);
    localStorage.setItem(localKey, serialized);
    sessionStorage.setItem(sessionKey, serialized);
  }
}

/**
 * Load filters with hybrid storage approach and business-unit scoping
 * - Universal filters: Sync from localStorage on first load, use sessionStorage on subsequent loads
 * - Page-specific filters: Read from sessionStorage
 * - All keys scoped by business unit
 *
 * Performance optimization: Uses module-level Set to track which BUs have been synced
 * This Set resets on page reload but persists during navigation within the same page load
 */
function loadFilters<T>(
  storageKey: string,
  defaultFilters: T,
  excludeFields: string[],
  universalFields: string[],
  businessUnitId: string,
): T {
  try {
    // Check TTL expiry
    if (isDataExpired(businessUnitId)) {
      clearExpiredFilters(businessUnitId);
      syncedBusinessUnits.delete(businessUnitId);
      return defaultFilters;
    }

    // Start with defaults and load page-specific filters
    let filters = { ...defaultFilters };
    const storedPageFilters = sessionStorage.getItem(storageKey);
    if (storedPageFilters) {
      const parsed = JSON.parse(storedPageFilters);
      // Merge stored values, respecting null as "explicitly cleared"
      Object.keys(parsed).forEach((key) => {
        if (parsed[key] === null) {
          // Explicitly cleared field - use undefined instead of default
          (filters as any)[key] = undefined;
        } else {
          // Has a value - use it
          (filters as any)[key] = parsed[key];
        }
      });
    }

    // Load universal filters with sync logic
    const hasSynced = syncedBusinessUnits.has(businessUnitId);
    universalFields.forEach((field) => {
      const result = loadUniversalFilter(businessUnitId, field, hasSynced);
      // result.wasCleared = true means field was explicitly cleared (should be undefined)
      // result.wasCleared = false means field was never set (use default)
      if (result.wasCleared) {
        filters[field as keyof T] = undefined as any;
      } else if (result.value !== undefined) {
        filters[field as keyof T] = result.value;
      }
    });

    // Mark as synced if valid businessUnitId
    if (!hasSynced && businessUnitId) {
      syncedBusinessUnits.add(businessUnitId);
    }

    return filters;
  } catch (error) {
    console.error('Failed to load filters:', error);
    return defaultFilters;
  }
}

/**
 * Check if data has expired for a specific business unit
 */
function isDataExpired(businessUnitId: string): boolean {
  const activityKey = getActivityKey(businessUnitId);
  const lastActivity = localStorage.getItem(activityKey);

  if (!lastActivity) return false;

  const lastActivityTime = parseInt(lastActivity, 10);
  const now = Date.now();
  const hoursSinceActivity = (now - lastActivityTime) / (1000 * 60 * 60);

  return hoursSinceActivity > TTL_HOURS;
}

/**
 * Load a single universal filter field with sync logic
 * - First load: sync from localStorage to sessionStorage
 * - Subsequent loads: read from sessionStorage only
 *
 * Returns an object with:
 * - value: the filter value (or undefined)
 * - wasCleared: true if field was explicitly cleared (value is null in storage)
 */
function loadUniversalFilter(
  businessUnitId: string,
  field: string,
  hasSynced: boolean,
): { value: any; wasCleared: boolean } {
  const filterType = getFilterType(field);
  const localKey = getUniversalFilterKey(businessUnitId, filterType);
  const sessionKey = getSessionUniversalFilterKey(businessUnitId, filterType);

  if (!hasSynced) {
    // First load after page reload - sync from localStorage (source of truth)
    const localValue = localStorage.getItem(localKey);

    if (localValue !== null) {
      // Sync to sessionStorage
      sessionStorage.setItem(sessionKey, localValue);

      try {
        const parsed = JSON.parse(localValue);
        // null in storage means explicitly cleared
        if (parsed === null) {
          return { value: undefined, wasCleared: true };
        }
        return { value: parsed, wasCleared: false };
      } catch (error) {
        console.error(`Failed to parse universal filter from local "${field}":`, error);
        return { value: undefined, wasCleared: false };
      }
    } else {
      // No key in localStorage means never set
      sessionStorage.removeItem(sessionKey);
      return { value: undefined, wasCleared: false };
    }
  } else {
    // Subsequent navigation - use sessionStorage (performance optimization)
    const sessionValue = sessionStorage.getItem(sessionKey);
    if (sessionValue !== null) {
      try {
        const parsed = JSON.parse(sessionValue);
        // null in storage means explicitly cleared
        if (parsed === null) {
          return { value: undefined, wasCleared: true };
        }
        return { value: parsed, wasCleared: false };
      } catch (error) {
        console.error(`Failed to parse universal filter from session "${field}":`, error);
        return { value: undefined, wasCleared: false };
      }
    }
  }

  return { value: undefined, wasCleared: false }; // Never set - use default
}

/**
 * Clear all expired filter data for a specific business unit from both storages
 */
function clearExpiredFilters(businessUnitId: string) {
  try {
    // Clear sessionStorage (page-specific filters + universal session copies for this BU)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (
        key.startsWith(`${STORAGE_KEY_PREFIXES.FILTERS}${businessUnitId}:`) ||
        key.startsWith(`${STORAGE_KEY_PREFIXES.SESSION_UNIVERSAL}${businessUnitId}:`)
      ) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear localStorage (universal filters + metadata for this BU)
    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key) => {
      if (
        key.startsWith(`${STORAGE_KEY_PREFIXES.UNIVERSAL}${businessUnitId}:`) ||
        key === getActivityKey(businessUnitId)
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear expired filters:', error);
  }
}
